/**
 * Arabic / English (ADR-0013). Resources are JSON files under src/locales/<lang>/<namespace>.json,
 * bundled eagerly (small). The choice lives in localStorage; Arabic is the default.
 * index.html sets <html lang dir> before first paint; `setLanguage` keeps it in sync.
 */
import i18n from "i18next"
import { initReactI18next } from "react-i18next"

export type Lang = "ar" | "en"
export const LANGS: Lang[] = ["ar", "en"]
const KEY = "uachieve.lang"

const files = import.meta.glob<{ default: Record<string, unknown> }>("../locales/*/*.json", { eager: true })
const resources: Record<string, Record<string, Record<string, unknown>>> = {}
for (const [path, mod] of Object.entries(files)) {
  const m = path.match(/locales\/(ar|en)\/([\w-]+)\.json$/)
  if (!m) continue
  resources[m[1]] ??= {}
  resources[m[1]][m[2]] = mod.default
}

function readLang(): Lang {
  try {
    return localStorage.getItem(KEY) === "en" ? "en" : "ar"
  } catch {
    return "ar"
  }
}

void i18n.use(initReactI18next).init({
  resources,
  lng: readLang(),
  fallbackLng: "ar",
  defaultNS: "common",
  ns: Object.keys(resources.ar ?? {}),
  interpolation: { escapeValue: false },
  returnNull: false,
})

export function currentLang(): Lang {
  return i18n.language === "en" ? "en" : "ar"
}

export function dirOf(lang: Lang): "rtl" | "ltr" {
  return lang === "ar" ? "rtl" : "ltr"
}

function applyLang(lang: Lang) {
  document.documentElement.lang = lang
  document.documentElement.dir = dirOf(lang)
}

/** Switch language, persist it and update <html lang dir>. Callers refetch server data afterwards. */
export async function setLanguage(lang: Lang) {
  try {
    localStorage.setItem(KEY, lang)
  } catch {
    // Storage blocked: applies for this page view only.
  }
  await i18n.changeLanguage(lang)
  applyLang(lang)
}

/** Pick the current language from a `{ar, en}` label (falls back to Arabic). */
export function localized(name: { ar: string; en?: string } | null | undefined): string {
  if (!name) return ""
  return (currentLang() === "en" ? name.en : name.ar) || name.ar
}

export default i18n
